import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Modal, Form, Button, Alert } from 'react-bootstrap';
import * as FaIcons from 'react-icons/fa';

const NuevoCierreModal = ({
  show,
  onHide,
  onSubmit,
  loading
}) => {
  const [formData, setFormData] = useState({
    cierre: '',
    fecha_inicio: '',
    fecha_termino: ''
  });
  const [errors, setErrors] = useState({});

  // Resetear formulario cuando se abre/cierra el modal
  React.useEffect(() => {
    if (show) {
      setFormData({
        cierre: '',
        fecha_inicio: '',
        fecha_termino: ''
      });
      setErrors({});
    }
  }, [show]);

  // Manejar cambios en los campos
  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Limpiar error del campo cuando el usuario empiece a escribir
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  // Validar formulario
  const validateForm = () => {
    const newErrors = {};

    if (!formData.cierre.trim()) {
      newErrors.cierre = 'El nombre del cierre es requerido';
    }

    if (!formData.fecha_inicio) {
      newErrors.fecha_inicio = 'La fecha de inicio es requerida';
    }

    if (!formData.fecha_termino) {
      newErrors.fecha_termino = 'La fecha de término es requerida';
    }

    if (formData.fecha_inicio && formData.fecha_termino) {
      const inicio = new Date(formData.fecha_inicio);
      const termino = new Date(formData.fecha_termino);
      
      if (inicio > termino) {
        newErrors.fecha_termino = 'La fecha de término debe ser posterior a la fecha de inicio';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Manejar envío del formulario
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    const cierreData = {
      cierre: formData.cierre.trim(),
      fecha_inicio: formData.fecha_inicio,
      fecha_termino: formData.fecha_termino,
      empresa_id: null,
      usuario_movil_id: null
    };

    await onSubmit(cierreData);
  };

  // Sugerir nombre basado en fechas
  const generarNombreSugerido = () => {
    if (formData.fecha_inicio && formData.fecha_termino) {
      const inicio = new Date(formData.fecha_inicio);
      const termino = new Date(formData.fecha_termino);
      
      const nombreMes = inicio.toLocaleDateString('es-CL', { month: 'long', year: 'numeric' });
      const nombreSugerido = `Cierre ${nombreMes.charAt(0).toUpperCase() + nombreMes.slice(1)}`;
      
      setFormData(prev => ({
        ...prev,
        cierre: nombreSugerido
      }));
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          <FaIcons.FaPlus className="me-2" />
          Crear Nuevo Cierre
        </Modal.Title>
      </Modal.Header>

      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          <Alert variant="info" className="mb-4">
            <FaIcons.FaInfoCircle className="me-2" />
            Complete la información para crear un nuevo cierre de conductores. 
            El proceso puede tomar entre 5 y 15 minutos dependiendo del volumen de datos.
          </Alert>

          {/* Nombre del Cierre */}
          <Form.Group className="mb-3">
            <Form.Label>
              <FaIcons.FaFileExcel className="me-2" />
              Nombre del Cierre *
            </Form.Label>
            <div className="d-flex gap-2">
              <Form.Control
                type="text"
                value={formData.cierre}
                onChange={(e) => handleChange('cierre', e.target.value)}
                placeholder="Ej: Cierre Julio 2025"
                isInvalid={!!errors.cierre}
                disabled={loading}
                maxLength={100}
              />
              <Button
                variant="outline-secondary"
                onClick={generarNombreSugerido}
                disabled={loading || !formData.fecha_inicio || !formData.fecha_termino}
                title="Generar nombre sugerido basado en fechas"
              >
                <FaIcons.FaMagic />
              </Button>
            </div>
            <Form.Control.Feedback type="invalid">
              {errors.cierre}
            </Form.Control.Feedback>
          </Form.Group>

          {/* Rango de Fechas */}
          <div className="row">
            <div className="col-md-6">
              <Form.Group className="mb-3">
                <Form.Label>
                  <FaIcons.FaCalendarAlt className="me-2" />
                  Fecha de Inicio *
                </Form.Label>
                <Form.Control
                  type="date"
                  value={formData.fecha_inicio}
                  onChange={(e) => handleChange('fecha_inicio', e.target.value)}
                  isInvalid={!!errors.fecha_inicio}
                  disabled={loading}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.fecha_inicio}
                </Form.Control.Feedback>
              </Form.Group>
            </div>

            <div className="col-md-6">
              <Form.Group className="mb-3">
                <Form.Label>
                  <FaIcons.FaCalendarAlt className="me-2" />
                  Fecha de Término *
                </Form.Label>
                <Form.Control
                  type="date"
                  value={formData.fecha_termino}
                  onChange={(e) => handleChange('fecha_termino', e.target.value)}
                  isInvalid={!!errors.fecha_termino}
                  disabled={loading}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.fecha_termino}
                </Form.Control.Feedback>
              </Form.Group>
            </div>
          </div>

          {/* Información adicional */}
          {formData.fecha_inicio && formData.fecha_termino && (
            <Alert variant="light" className="border">
              <div className="d-flex align-items-start gap-2">
                <FaIcons.FaInfoCircle className="text-info mt-1" />
                <div>
                  <strong>Resumen del Cierre:</strong>
                  <ul className="mb-0 mt-2">
                    <li>Periodo: {formData.fecha_inicio} al {formData.fecha_termino}</li>
                    <li>Tipo: Cierre general (todas las empresas y conductores)</li>
                    <li>Formato: Archivo Excel con reporte completo</li>
                  </ul>
                </div>
              </div>
            </Alert>
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button
            variant="outline-secondary"
            onClick={onHide}
            disabled={loading}
          >
            Cancelar
          </Button>
          <Button
            variant="primary"
            type="submit"
            disabled={loading}
          >
            {loading ? (
              <>
                <div className="spinner-border spinner-border-sm me-2" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                Creando Cierre...
              </>
            ) : (
              <>
                <FaIcons.FaCheck className="me-2" />
                Crear Cierre
              </>
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

NuevoCierreModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  loading: PropTypes.bool
};

NuevoCierreModal.defaultProps = {
  loading: false
};

export default NuevoCierreModal;
